import React, { useState, useEffect, useCallback, useMemo } from "react";
import "./BranchDetailLedger.css";
import "./BranchHistoryLedger.css";

const TX_API = `https://${process.env.REACT_APP_API_KEY_TRANS}.execute-api.ap-south-1.amazonaws.com/default/branchTransaction`;
const SUMMARY_API = `https://${process.env.REACT_APP_API_KEY_SUMMARY}.execute-api.ap-south-1.amazonaws.com/default/getAllBranchSummary`;
const IST_OFFSET_MS = 5.5 * 60 * 60 * 1000;

function fmt(n) {
  return Number(n || 0).toLocaleString("en-IN");
}

function getISTDateString(isoDate) {
  const d = new Date(isoDate);
  return d.toLocaleDateString("en-IN", {
    timeZone: "Asia/Kolkata",
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

const Remark = ({ text }) => {
  const [expanded, setExpanded] = useState(false);
  if (!text) return null;
  const LIMIT = 20;
  if (text.length <= LIMIT) return <span>{text}</span>;
  return (
    <span>
      {expanded ? text : text.slice(0, LIMIT) + "…"}
      <button
        style={{
          background: "none",
          border: "none",
          color: "#4361ee",
          fontSize: "0.68rem",
          cursor: "pointer",
          textDecoration: "underline",
          marginLeft: 2,
        }}
        onClick={() => setExpanded((p) => !p)}
      >
        {expanded ? "less" : "more"}
      </button>
    </span>
  );
};

export default function BranchHistoryLedger({
  branchId,
  grantedDates,
  onClose,
}) {
  const sortedDates = useMemo(
    () => [...grantedDates].sort((a, b) => new Date(b) - new Date(a)),
    [grantedDates],
  );
  const [activeDate, setActiveDate] = useState(sortedDates[0] || "");
  const [transactions, setTransactions] = useState([]);
  const [opBal, setOpBal] = useState(0);
  const [branchSum, setBranchSum] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchTxns = useCallback(
    async (date) => {
      if (!date || !branchId) return;
      setLoading(true);
      try {
        const istMidnightUTC = new Date(
          new Date(`${date}T00:00:00.000Z`).getTime() - IST_OFFSET_MS,
        ).toISOString();
        const istEndUTC = new Date(
          new Date(`${date}T00:00:00.000Z`).getTime() -
            IST_OFFSET_MS +
            86400000 -
            1,
        ).toISOString();

        const [sRes, sumRes] = await Promise.all([
          fetch(TX_API, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              action: "fetchTransactions",
              branchId,
              from: istMidnightUTC,
              to: istEndUTC,
            }),
          }),
          fetch(`${SUMMARY_API}?start=${date}&end=${date}`),
        ]);

        const sData = await sRes.json();
        const sumData = await sumRes.json();
        setTransactions(sData.transactions || []);
        const branchSum = (sumData.summary || {})[branchId];
        setOpBal(branchSum?.opBal ?? 0);
        setBranchSum(branchSum || null);
      } catch (err) {
        console.error("History fetch error:", err);
      } finally {
        setLoading(false);
      }
    },
    [branchId],
  );

  useEffect(() => {
    if (activeDate) fetchTxns(activeDate);
  }, [activeDate, fetchTxns]);

  const data = useMemo(() => {
    const branchTx = transactions.filter((tx) => {
      const isMyRole =
        (tx.fromBranch === branchId && tx.flowRole === "SENDER") ||
        (tx.toBranch === branchId && tx.flowRole === "RECEIVER") ||
        tx.thirdParty === branchId;
      return isMyRole && tx.status !== "REJECTED" && tx.status !== "SPLIT";
    });

    const inflow = [],
      outflow = [],
      btIn = [],
      btOut = [],
      expenses = [];
    let totIn = 0,
      totOut = 0,
      totBtIn = 0,
      totBtOut = 0,
      totExp = 0,
      totComm = 0;

    branchTx.forEach((tx) => {
      const amt = Number(tx.amount || 0);
      const comm = Number(tx.commission || 0);
      const isTP = tx.thirdParty && tx.thirdParty !== "NONE";
      const isThisTP = tx.thirdParty === branchId;

      if (["BTIN", "BTSPLITIN"].includes(tx.paymentType)) {
        btIn.push(tx);
        if (!isThisTP) totBtIn += amt;
      } else if (["BTOUT", "BTSPLITOUT"].includes(tx.paymentType)) {
        btOut.push(tx);
        if (!isThisTP) totBtOut += amt;
      } else if (tx.paymentType === "EXP") {
        expenses.push(tx);
        totExp += amt;
      } else if (["IN", "SPLITIN"].includes(tx.paymentType)) {
        inflow.push(tx);
        if (!isThisTP) totIn += amt;
      } else if (["OUT", "SPLITOUT"].includes(tx.paymentType)) {
        outflow.push(tx);
        if (!isThisTP) totOut += amt;
      }
      if (isThisTP) totComm += comm;
      else if (!isTP && comm > 0) totComm += comm;
    });

    return {
      inflow,
      outflow,
      btIn,
      btOut,
      expenses,
      totIn,
      totOut,
      totBtIn,
      totBtOut,
      totExp,
      totComm,
    };
  }, [transactions, branchId]);

  const maxRows = Math.max(data.inflow.length, data.outflow.length, 1);

  return (
    <div className="ledger-container">
      {/* Header */}
      <div
        className="ledger-header-banner"
        style={{ justifyContent: "space-between" }}
      >
        <div>
          <h2 style={{ margin: 0 }}>Branch History — {branchId}</h2>
          <span style={{ fontSize: "0.75rem", opacity: 0.7 }}>
            Read-only view of approved past dates
          </span>
        </div>
        <button
          onClick={onClose}
          style={{
            background: "rgba(255,255,255,0.15)",
            border: "1px solid rgba(255,255,255,0.3)",
            color: "#fff",
            borderRadius: 8,
            padding: "6px 16px",
            cursor: "pointer",
            fontWeight: 600,
          }}
        >
          ✕ Close
        </button>
      </div>

      {/* Date Tabs */}
      <div className="history-date-tabs">
        {sortedDates.map((d) => (
          <button
            key={d}
            className={`history-date-tab ${activeDate === d ? "active" : ""}`}
            onClick={() => setActiveDate(d)}
          >
            {getISTDateString(d + "T00:00:00.000Z")}
          </button>
        ))}
      </div>

      {loading ? (
        <div style={{ textAlign: "center", padding: 40, color: "#64748b" }}>
          Loading transactions…
        </div>
      ) : (
        <div className="ledger-grid">
          {/* Cash Transactions */}
          <div className="ledger-section-card">
            <div className="ledger-section-title">Cash Transactions</div>
            <div style={{ overflowX: "auto" }}>
              <table className="excel-table">
                <thead>
                  <tr>
                    <th className="col-sno">#</th>
                    <th>Name (IN)</th>
                    <th>IN ₹</th>
                    <th>COM</th>
                    <th>Remarks</th>
                    <th className="col-divider"></th>
                    <th>Name (OUT)</th>
                    <th>OUT ₹</th>
                    <th>COM</th>
                    <th>Remarks</th>
                  </tr>
                </thead>
                <tbody>
                  {Array.from({ length: maxRows }).map((_, i) => {
                    const inItem = data.inflow[i];
                    const outItem = data.outflow[i];
                    const getPartnerIn = (tx) =>
                      !tx
                        ? ""
                        : tx.toBranch === branchId
                          ? tx.fromBranch
                          : tx.toBranch;
                    const getPartnerOut = (tx) =>
                      !tx
                        ? ""
                        : tx.fromBranch === branchId
                          ? tx.toBranch
                          : tx.fromBranch;
                    return (
                      <tr key={i}>
                        <td className="col-sno">{i + 1}</td>
                        <td className="col-in-name">
                          {inItem ? getPartnerIn(inItem) : ""}
                        </td>
                        <td className="col-in-amt">
                          {inItem ? fmt(inItem.amount) : ""}
                        </td>
                        <td className="col-in-comm">
                          {inItem?.commission || ""}
                        </td>
                        <td className="col-remarks">
                          {inItem ? <Remark text={inItem.remarks} /> : ""}
                        </td>
                        <td className="col-divider"></td>
                        <td className="col-out-name">
                          {outItem ? getPartnerOut(outItem) : ""}
                        </td>
                        <td className="col-out-amt">
                          {outItem ? fmt(outItem.amount) : ""}
                        </td>
                        <td className="col-out-comm">
                          {outItem?.commission || ""}
                        </td>
                        <td className="col-remarks">
                          {outItem ? <Remark text={outItem.remarks} /> : ""}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Daily Summary */}
          <div className="ledger-section-card">
            <div className="ledger-section-title">Daily Summary</div>
            <table className="summary-table">
              <tbody>
                {(() => {
                  const sIn = branchSum?.totalIn ?? data.totIn;
                  const sBtIn = branchSum?.btIn ?? data.totBtIn;
                  const sOut = branchSum?.totalOut ?? data.totOut;
                  const sBtOut = branchSum?.btOut ?? data.totBtOut;
                  const sComm = branchSum?.comm ?? data.totComm;
                  const sExp = branchSum?.exp ?? data.totExp;
                  const closing = branchSum?.closingBal ?? (opBal + sIn + sBtIn - sOut - sBtOut - sExp + sComm);
                  return (
                    <>
                      <tr className="summary-opening-row">
                        <td className="s-label">Opening Balance</td>
                        <td className="s-val">
                          {opBal < 0 ? "-" : ""}₹{fmt(Math.abs(opBal))}
                        </td>
                      </tr>
                      <tr>
                        <td className="s-label">Cash IN</td>
                        <td className="s-val green">₹{fmt(sIn)}</td>
                      </tr>
                      <tr>
                        <td className="s-label">BT IN (+)</td>
                        <td className="s-val green">₹{fmt(sBtIn)}</td>
                      </tr>
                      <tr>
                        <td className="s-label">Cash OUT</td>
                        <td className="s-val red">₹{fmt(sOut)}</td>
                      </tr>
                      <tr>
                        <td className="s-label">BT OUT (−)</td>
                        <td className="s-val red">₹{fmt(sBtOut)}</td>
                      </tr>
                      <tr>
                        <td className="s-label">Commission</td>
                        <td className="s-val blue">₹{fmt(sComm)}</td>
                      </tr>
                      <tr>
                        <td className="s-label">Expenses</td>
                        <td className="s-val orange">₹{fmt(sExp)}</td>
                      </tr>
                      <tr className="summary-closing-row">
                        <td className="s-label">Closing Balance</td>
                        <td className={`s-val ${closing < 0 ? "red" : "blue"}`}>
                          ₹{fmt(closing)}
                        </td>
                      </tr>
                    </>
                  );
                })()}
              </tbody>
            </table>
          </div>

          {/* BT Details */}
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <div className="ledger-section-card">
              <div className="ledger-section-title">BT IN Details</div>
              <table className="excel-table bt-table">
                <thead>
                  <tr>
                    <th>Route</th>
                    <th style={{ textAlign: "right" }}>Amount</th>
                    <th>Remarks</th>
                  </tr>
                </thead>
                <tbody>
                  {data.btIn.length === 0 && (
                    <tr>
                      <td
                        colSpan="3"
                        style={{
                          color: "#94a3b8",
                          textAlign: "center",
                          padding: 8,
                        }}
                      >
                        No BT IN
                      </td>
                    </tr>
                  )}
                  {data.btIn.map((tx, i) => (
                    <tr key={i}>
                      <td>
                        {tx.fromBranch} → {tx.toBranch}
                      </td>
                      <td
                        style={{
                          textAlign: "right",
                          color: "#16a34a",
                          fontWeight: 700,
                        }}
                      >
                        ₹{fmt(tx.amount)}
                      </td>
                      <td className="col-remarks">
                        <Remark text={tx.remarks} />
                      </td>
                    </tr>
                  ))}
                  <tr className="bt-total-row">
                    <td>Total BT IN</td>
                    <td>₹{fmt(data.totBtIn)}</td>
                    <td></td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div className="ledger-section-card">
              <div className="ledger-section-title">BT OUT Details</div>
              <table className="excel-table bt-table">
                <thead>
                  <tr>
                    <th>Route</th>
                    <th style={{ textAlign: "right" }}>Amount</th>
                    <th>Remarks</th>
                  </tr>
                </thead>
                <tbody>
                  {data.btOut.length === 0 && (
                    <tr>
                      <td
                        colSpan="3"
                        style={{
                          color: "#94a3b8",
                          textAlign: "center",
                          padding: 8,
                        }}
                      >
                        No BT OUT
                      </td>
                    </tr>
                  )}
                  {data.btOut.map((tx, i) => (
                    <tr key={i}>
                      <td>
                        {tx.fromBranch} → {tx.toBranch}
                      </td>
                      <td
                        style={{
                          textAlign: "right",
                          color: "#dc2626",
                          fontWeight: 700,
                        }}
                      >
                        ₹{fmt(tx.amount)}
                      </td>
                      <td className="col-remarks">
                        <Remark text={tx.remarks} />
                      </td>
                    </tr>
                  ))}
                  <tr className="bt-total-row">
                    <td>Total BT OUT</td>
                    <td>₹{fmt(data.totBtOut)}</td>
                    <td></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
